import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { STATUS_LABEL } from "@/lib/roMeta";
import { GitCompare, Upload, CheckCircle2, AlertTriangle } from "lucide-react";

const NORM = (s) => (s || "").toUpperCase().replace(/[—–]/g, "-").replace(/\s+/g, " ").trim();
const STATUS_FROM = {
  "READY TO SUBMIT": "ready_to_submit",
  "READY - PENDING MANUAL VERIFICATION": "pending_manual",
  "REVIEW BEFORE SUBMISSION": "review",
  "REQUIRED BEFORE SUBMISSION": "required",
  "SKIP - MAINTENANCE": "skip_maintenance",
};

function parseCSV(text) {
  text = text.replace(/^\ufeff/, "");
  const rows = [];
  let row = [], cur = "", q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) {
      if (c === '"') { if (text[i + 1] === '"') { cur += '"'; i++; } else q = false; }
      else cur += c;
    } else if (c === '"') q = true;
    else if (c === ",") { row.push(cur); cur = ""; }
    else if (c === "\n") { row.push(cur); rows.push(row); row = []; cur = ""; }
    else if (c !== "\r") cur += c;
  }
  if (cur.length || row.length) { row.push(cur); rows.push(row); }
  return rows.filter((r) => r.some((x) => (x || "").trim() !== ""));
}

const findCol = (headers, ...names) => {
  const h = headers.map((x) => NORM(x));
  for (const n of names) { const i = h.indexOf(NORM(n)); if (i >= 0) return i; }
  for (let i = 0; i < h.length; i++) if (names.some((n) => h[i].includes(NORM(n)))) return i;
  return -1;
};

export function CompareDialog({ records = [] }) {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    try {
      const rows = parseCSV(await file.text());
      if (rows.length < 2) { setError("CSV appears empty."); return; }
      const headers = rows[0];
      const iRo = findCol(headers, "ro", "repair order", "ro number", "ro #");
      const iStatus = findCol(headers, "status");
      const iScore = findCol(headers, "score");
      if (iRo < 0 || iStatus < 0) { setError("Could not find RO and Status columns in the CSV."); return; }
      const appMap = {};
      records.forEach((r) => { if (r.ro_number) appMap[String(r.ro_number).trim()] = r; });
      const diffs = [], onlyInCsv = [];
      let matched = 0, agree = 0;
      const csvRos = new Set();
      for (let i = 1; i < rows.length; i++) {
        const ro = (rows[i][iRo] || "").trim();
        if (!ro) continue;
        csvRos.add(ro);
        const theirStatus = STATUS_FROM[NORM(rows[i][iStatus])] || NORM(rows[i][iStatus]);
        const theirScore = iScore >= 0 ? (rows[i][iScore] || "").trim() : null;
        const app = appMap[ro];
        if (!app) { onlyInCsv.push({ ro, theirStatus, theirScore }); continue; }
        matched += 1;
        const statusDiff = app.compliance_status !== theirStatus;
        const scoreDiff = theirScore !== null && theirScore !== "" && String(app.score) !== String(Number(theirScore));
        if (statusDiff || scoreDiff) diffs.push({ ro, theirStatus, theirScore, app, statusDiff, scoreDiff });
        else agree += 1;
      }
      const onlyInApp = records.filter((r) => r.ro_number && !csvRos.has(String(r.ro_number).trim()));
      setResult({ matched, agree, diffs, onlyInCsv, onlyInApp, total: rows.length - 1 });
    } catch (err) {
      setError("Could not read the CSV file.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setResult(null); setError(""); } }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="lg" data-testid="compare-open-button"><GitCompare className="h-4 w-4" /> Compare with Old Tool</Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl bg-card" data-testid="compare-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-extrabold uppercase tracking-tight">Compare with Old Tool</DialogTitle>
          <DialogDescription>Upload the CSV your old dashboard exported. We match by RO number and flag any row where the status or score differs.</DialogDescription>
        </DialogHeader>

        <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-secondary/30 px-4 py-6 text-sm hover:border-primary" data-testid="compare-dropzone">
          <Upload className="h-4 w-4" /> Choose your old tool's CSV export
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="compare-file-input" />
        </label>
        {error && <p className="text-sm text-red-400" data-testid="compare-error">{error}</p>}

        {result && (
          <div className="space-y-4" data-testid="compare-summary">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Matched ROs" value={result.matched} />
              <Stat label="Agree" value={result.agree} tone="text-emerald-400" />
              <Stat label="Differ" value={result.diffs.length} tone={result.diffs.length ? "text-amber-400" : "text-emerald-400"} />
              <Stat label="Unmatched" value={result.onlyInCsv.length + result.onlyInApp.length} tone="text-muted-foreground" />
            </div>

            {result.diffs.length === 0 ? (
              <p className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-300" data-testid="compare-allmatch">
                <CheckCircle2 className="h-4 w-4" /> Every matched RO scored identically to your old tool.
              </p>
            ) : (
              <div className="max-h-72 overflow-auto rounded-lg border border-border">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead className="font-mono text-[11px] uppercase">RO</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase">Old Tool</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase">This App</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {result.diffs.map((d) => (
                      <TableRow key={d.ro} data-testid={`compare-diff-row-${d.ro}`}>
                        <TableCell className="font-mono font-bold">{d.ro}</TableCell>
                        <TableCell className="text-xs">
                          <span className={d.statusDiff ? "text-amber-300" : ""}>{STATUS_LABEL[d.theirStatus] || d.theirStatus}</span>
                          {d.theirScore !== null && <span className={`ml-2 font-mono ${d.scoreDiff ? "text-amber-300" : "text-muted-foreground"}`}>{d.theirScore}</span>}
                        </TableCell>
                        <TableCell className="text-xs">
                          <span className={d.statusDiff ? "text-amber-300" : ""}>{STATUS_LABEL[d.app.compliance_status] || d.app.compliance_status}</span>
                          <span className={`ml-2 font-mono ${d.scoreDiff ? "text-amber-300" : "text-muted-foreground"}`}>{d.app.score == null ? "—" : d.app.score}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {(result.onlyInCsv.length > 0 || result.onlyInApp.length > 0) && (
              <p className="flex items-start gap-2 text-xs text-muted-foreground" data-testid="compare-unmatched-note">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {result.onlyInCsv.length} RO(s) in the CSV aren't loaded here, and {result.onlyInApp.length} RO(s) here weren't in the CSV. Load the same batch in both tools for a full comparison.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Stat = ({ label, value, tone = "text-foreground" }) => (
  <div className="rounded-lg border border-border bg-secondary/30 px-3 py-2">
    <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`text-2xl font-extrabold ${tone}`}>{value}</div>
  </div>
);
